import { cn } from "@/lib/utils"
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  Children,
  type FC,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react"

type HorizontalSliderProps = {
  children: ReactNode
  labels?: { previous: string; next: string }
  /** Width classes for each item, e.g. how many fit per view. */
  itemClassName?: string
  className?: string
}

const arrowClassName =
  "absolute top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-11 h-11 rounded-full bg-white text-[#0077B6] shadow-lg border border-gray-200 hover:bg-[#0077B6] hover:text-white transition-colors"

export const HorizontalSlider: FC<HorizontalSliderProps> = ({
  children,
  labels = { previous: "Previous", next: "Next" },
  itemClassName = "basis-[85%] sm:basis-[calc((100%_-_1.5rem)/2)] lg:basis-[calc((100%_-_4.5rem)/4)]",
  className,
}) => {
  const scrollerRef = useRef<HTMLDivElement>(null)
  const [canPrev, setCanPrev] = useState(false)
  const [canNext, setCanNext] = useState(false)

  const updateArrows = useCallback((): void => {
    const scroller = scrollerRef.current
    if (scroller === null) {
      return
    }
    setCanPrev(scroller.scrollLeft > 4)
    setCanNext(
      scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 4,
    )
  }, [])

  useEffect(() => {
    const scroller = scrollerRef.current
    if (scroller === null) {
      return
    }
    updateArrows()
    const observer = new ResizeObserver(updateArrows)
    observer.observe(scroller)
    return () => observer.disconnect()
  }, [updateArrows, children])

  const scrollByPage = (direction: number): void => {
    const scroller = scrollerRef.current
    scroller?.scrollBy({
      left: direction * scroller.clientWidth,
      behavior: "smooth",
    })
  }

  return (
    <div className={cn("relative", className)}>
      <div
        ref={scrollerRef}
        onScroll={updateArrows}
        className="flex gap-6 overflow-x-auto snap-x snap-mandatory scroll-smooth -my-4 py-4 px-1 -mx-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {Children.map(children, (child) => (
          <div className={cn("snap-start shrink-0", itemClassName)}>
            {child}
          </div>
        ))}
      </div>

      {canPrev && (
        <button
          type="button"
          onClick={() => scrollByPage(-1)}
          aria-label={labels.previous}
          className={cn(arrowClassName, "left-1 lg:left-0 lg:-translate-x-1/2")}
        >
          <FontAwesomeIcon icon={faChevronLeft} className="w-4 h-4" />
        </button>
      )}
      {canNext && (
        <button
          type="button"
          onClick={() => scrollByPage(1)}
          aria-label={labels.next}
          className={cn(
            arrowClassName,
            "right-1 lg:right-0 lg:translate-x-1/2",
          )}
        >
          <FontAwesomeIcon icon={faChevronRight} className="w-4 h-4" />
        </button>
      )}
    </div>
  )
}
